import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Reveal, SectionHeading } from "../components/Reveal";
import { BookingForm } from "../components/BookingForm";
import { SKIPS, scrollToId } from "../data/content";

const SkipCard = ({ skip, onBook }) => (
  <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-ink/25 hover:shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
    <div className="mb-6 flex h-36 items-center justify-center rounded-xl bg-black/[0.02] p-4">
      <img
        src={skip.img}
        alt={`${skip.name} skip`}
        className="max-h-full w-auto object-contain"
      />
    </div>
    <div className="flex items-center justify-between gap-3">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">{skip.tag}</p>
      {skip.popular && (
        <span className="rounded-full bg-ink px-3 py-1 font-mono text-[10px] tracking-[0.15em] text-white">
          POPULAR
        </span>
      )}
    </div>
    <h3 className="mt-3 font-heading text-2xl font-extrabold text-ink">{skip.name}</h3>
    {skip.price ? (
      <p className="mt-4">
        <span className="font-heading text-4xl font-black tracking-tight text-ink">
          {skip.priceLabel}
        </span>{" "}
        <span className="text-xs text-ink/50">all-in, inc. VAT</span>
      </p>
    ) : (
      <p className="mt-4 font-heading text-2xl font-extrabold text-ink">Enquiry only</p>
    )}
    <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/65">{skip.note}</p>
    <p className="mt-4 border-t border-ink/10 pt-4 text-xs text-ink/55">
      Holds approx. {skip.bags}
      {skip.price ? " · 5-day hire · 3-tonne limit" : ""}
    </p>
    {skip.price ? (
      <button
        data-testid={`skip-card-select-${skip.id}`}
        onClick={() => onBook(skip.id)}
        className="mt-6 w-full rounded-full bg-ink py-3 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-emerald-800"
      >
        Book &amp; Pay {skip.priceLabel}
      </button>
    ) : (
      <a
        data-testid={`skip-card-select-${skip.id}`}
        href={`/contact?subject=${encodeURIComponent(`Skip enquiry — ${skip.name}`)}`}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 py-3 font-heading text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
      >
        Send an Enquiry
      </a>
    )}
  </div>
);

export default function PricesPage() {
  const [selectedSkip, setSelectedSkip] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#booking") {
      const t = setTimeout(() => scrollToId("booking"), 150);
      return () => clearTimeout(t);
    }
  }, [location]);

  const onBook = (id) => {
    setSelectedSkip(id);
    scrollToId("booking");
  };

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <SectionHeading
          eyebrow="Prices"
          title="Two online prices. Bigger skips by enquiry."
          sub="The 4 and 6 cubic yard book and pay online — 5-day hire, 3-tonne limit and free collection included. For the 8, 14 and roll-ons, send us an enquiry and we'll come straight back with a price."
        />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SKIPS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06} className="h-full">
              <SkipCard skip={s} onBook={onBook} />
            </Reveal>
          ))}
        </div>
      </section>

      <section id="booking" className="border-t border-ink/10 bg-black/[0.02]">
        <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
          <SectionHeading
            eyebrow="Book online"
            title="Book & pay in two minutes."
            sub="Pick a date, tell us where, pay by card. We'll call to confirm your delivery slot within the hour (Mon–Sat, 7am–7pm)."
          />
          <Reveal>
            <BookingForm selectedSkip={selectedSkip} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
